import { useMemo, useState } from 'react';
import { ListTable, ScreenFrame } from '@/components/Screen';
import { FilterCard, MetricCard, MetricGrid, SectionCard } from '@/components/DataList';
import { Icon } from '@/components/icons';
import { admin } from '@/api/endpoints';
import { errorMessage, pickList, type JsonMap } from '@/api/client';
import { useAsync } from '@/hooks/useAsync';
import {
  Button,
  Empty,
  ErrorBox,
  Form,
  Input,
  Loading,
  Modal,
  ProSearch,
  Select,
  StatusBadge,
} from '@/components/ui';
import { formatDateTime, str } from '@/utils/format';
import { UssdCatalogPanels } from '@/screens/admin/UssdCatalog';

const MATCH_LABELS: Record<string, string> = {
  contains: 'يحتوي',
  starts_with: 'يبدأ بـ',
  regex: 'Regex',
};

const OP_LABELS: Record<string, string> = {
  '*': 'كل الخدمات',
  units: 'وحدات',
  bills: 'فاتورة',
  cash: 'كاش',
};

function emptyRule(): JsonMap {
  return {
    id: 0,
    company: 'syriatel',
    operation_kind: '*',
    match_type: 'contains',
    pattern: '',
    result_type: 'success',
    require_target: 1,
    is_active: 1,
    sort_order: 0,
  };
}

function simReady(device: JsonMap, sim: JsonMap): boolean {
  return Number(device.is_online) === 1 && Number(device.is_active) !== 0 && Number(sim.is_active) === 1 && Number(sim.has_secret) === 1;
}

export function ExecutorScreen() {
  const state = useAsync(
    async () => {
      const [devices, rules] = await Promise.all([admin.executorDevices(), admin.ussdRules()]);
      if (!devices.success) return devices;
      return {
        success: true as const,
        devices: pickList(devices, 'data', 'devices', 'items'),
        rules: rules.success ? pickList(rules, 'data', 'rules', 'items') : [],
      };
    },
    (p) => ({
      devices: pickList(p, 'devices').length ? pickList(p, 'devices') : Array.isArray(p.devices) ? (p.devices as JsonMap[]) : [],
      rules: Array.isArray(p.rules) ? (p.rules as JsonMap[]) : [],
    }),
  );

  const [q, setQ] = useState('');
  const [deviceEdits, setDeviceEdits] = useState<Record<number, JsonMap>>({});
  const [simEdits, setSimEdits] = useState<Record<number, JsonMap>>({});
  const [ruleOpen, setRuleOpen] = useState(false);
  const [ruleForm, setRuleForm] = useState<JsonMap>(() => emptyRule());
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState(false);

  const devices = state.data?.devices ?? [];
  const rules = state.data?.rules ?? [];

  const filteredDevices = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return devices;
    return devices.filter((d) => {
      const simHay = (Array.isArray(d.sims) ? (d.sims as JsonMap[]) : [])
        .map((s) => str(s.sim_label))
        .join(' ');
      const hay = [str(d.device_name), str(d.manufacturer), str(d.model), simHay].join(' ').toLowerCase();
      return hay.includes(needle);
    });
  }, [devices, q]);

  const onlineCount = devices.filter((d) => Number(d.is_online) === 1).length;
  const readySims = devices.reduce((acc, d) => {
    const sims = Array.isArray(d.sims) ? (d.sims as JsonMap[]) : [];
    return acc + sims.filter((s) => simReady(d, s)).length;
  }, 0);

  function deviceDraft(d: JsonMap): JsonMap {
    const id = Number(d.id);
    return deviceEdits[id] ?? { device_name: d.device_name, is_active: d.is_active };
  }

  function patchDevice(id: number, d: JsonMap, patch: JsonMap) {
    setDeviceEdits((prev) => ({ ...prev, [id]: { ...deviceDraft(d), ...patch } }));
  }

  function simDraft(sim: JsonMap): JsonMap {
    const id = Number(sim.id);
    return (
      simEdits[id] ?? {
        sim_label: sim.sim_label,
        company: sim.company ?? 'other',
        is_active: sim.is_active,
        secret_code: '',
      }
    );
  }

  function patchSim(id: number, sim: JsonMap, patch: JsonMap) {
    setSimEdits((prev) => ({ ...prev, [id]: { ...simDraft(sim), ...patch } }));
  }

  async function disableDevice(device: JsonMap) {
    if (
      !window.confirm(
        'منع الجهاز من استلام طلبات جديدة؟ أي عملية بدأت فعلياً ستنتظر نتيجتها ولن تُلغى من الشبكة.',
      )
    ) {
      return;
    }
    setBusy(true);
    const body = deviceDraft(device);
    const res = await admin.saveExecutorDevice({
      device_id: device.id,
      device_name: body.device_name,
      is_active: 0,
    });
    setBusy(false);
    setMsg(res.success ? str(res.message, 'تم إيقاف الجهاز.') : errorMessage(res));
    if (res.success) state.reload();
  }

  async function saveDevice(device: JsonMap) {
    const id = Number(device.id);
    const body = deviceDraft(device);
    setBusy(true);
    const res = await admin.saveExecutorDevice({
      device_id: id,
      device_name: body.device_name,
      is_active: body.is_active,
    });
    setBusy(false);
    setMsg(res.success ? str(res.message, 'تم حفظ الجهاز.') : errorMessage(res));
    if (res.success) {
      setDeviceEdits((prev) => {
        const next = { ...prev };
        delete next[id];
        return next;
      });
      state.reload();
    }
  }

  async function deleteDevice(device: JsonMap) {
    if (
      !window.confirm(
        'حذف الجهاز من القائمة ومنع التنفيذ عليه؟ سيبقى تاريخه محفوظاً ويمكن استعادته لاحقاً.',
      )
    ) {
      return;
    }
    setBusy(true);
    const res = await admin.deleteExecutorDevice({ device_id: device.id, id: device.id });
    setBusy(false);
    if (!res.success) {
      setMsg(errorMessage(res));
      return;
    }
    state.reload();
  }

  async function restoreDevice(device: JsonMap) {
    if (!window.confirm('استعادة الجهاز مع إبقائه معطلاً إلى أن تفعّله؟')) return;
    setBusy(true);
    const res = await admin.saveExecutorDevice({ device_id: device.id, restore: true, is_active: 0 });
    setBusy(false);
    setMsg(res.success ? str(res.message, 'تمت الاستعادة.') : errorMessage(res));
    if (res.success) state.reload();
  }

  async function saveSim(sim: JsonMap) {
    const body = simDraft(sim);
    setBusy(true);
    const res = await admin.saveExecutorSim({
      sim_id: sim.id,
      company: body.company,
      sim_label: body.sim_label,
      is_active: body.is_active,
      secret_code: body.secret_code,
    });
    setBusy(false);
    setMsg(res.success ? str(res.message, 'تم حفظ الشريحة.') : errorMessage(res));
    if (res.success) {
      setSimEdits((prev) => {
        const next = { ...prev };
        delete next[Number(sim.id)];
        return next;
      });
      state.reload();
    }
  }

  async function saveRule() {
    if (!str(ruleForm.pattern, '').trim()) {
      setMsg('أدخل النص الذي يدل على النتيجة.');
      return;
    }
    setBusy(true);
    const res = await admin.saveUssdRule({
      ...ruleForm,
      require_target: ruleForm.require_target ? 1 : 0,
      is_active: ruleForm.is_active ? 1 : 0,
    });
    setBusy(false);
    setMsg(res.success ? str(res.message, 'تم حفظ القاعدة.') : errorMessage(res));
    if (res.success) {
      setRuleOpen(false);
      setRuleForm(emptyRule());
      state.reload();
    }
  }

  async function deleteRule(id: unknown) {
    if (!window.confirm('حذف قاعدة الرد هذه؟')) return;
    const res = await admin.deleteUssdRule({ id });
    if (!res.success) {
      setMsg(errorMessage(res));
      return;
    }
    state.reload();
  }

  if (state.loading) return <Loading />;
  if (state.error) return <ErrorBox message={state.error} onRetry={state.reload} />;

  return (
    <ScreenFrame
      mode="admin"
      icon="device"
      title="منفذ USSD"
      subtitle="إدارة أجهزة التنفيذ والشرائح المتصلة وقواعد قبول ردود الشبكة."
      actions={
        <Button type="button" variant="secondary" onClick={() => state.reload()}>
          <Icon name="refresh" size={18} /> تحديث الحالة
        </Button>
      }
    >
      <MetricGrid>
        <MetricCard title="أجهزة مسجّلة" value={String(devices.length)} icon={<Icon name="device" size={21} />} />
        <MetricCard title="متصل بالسيرفر" value={String(onlineCount)} icon={<Icon name="check" size={21} />} />
        <MetricCard title="شرائح جاهزة" value={String(readySims)} icon={<Icon name="wallet" size={21} />} />
        <MetricCard title="قواعد الرد" value={String(rules.length)} icon={<Icon name="shield" size={21} />} />
      </MetricGrid>

      {msg ? <p className={msg.includes('أدخل') || msg.includes('تعذر') ? 'error' : 'ok'}>{msg}</p> : null}

      <SectionCard
        title="منفّذ USSD على هذا الهاتف"
        subtitle="تفعيل صريح من الهاتف؛ فتح هذه الصفحة وحده لا يشغّل المنفّذ."
        icon={<Icon name="device" size={24} />}
      >
        <div className="executor-status">
          <strong>أنت تتصفح الإدارة من الويب</strong>
          <small className="field-hint">
            منح صلاحيات الاتصال، البطارية، الإشعارات، الرسائل، والإيقاف المحلي متاحة من تطبيق الإدارة على
            أندرويد فقط.
          </small>
        </div>
      </SectionCard>

      <SectionCard
        title="قواعد ردود USSD"
        subtitle="الرد المطابق لقاعدة نجاح يُعتمد ناجحاً؛ وأي رد نهائي لا يطابق قاعدة نجاح يعتبر فاشلاً."
        icon={<Icon name="shield" size={24} />}
        actions={
          <Button
            type="button"
            onClick={() => {
              setRuleForm(emptyRule());
              setRuleOpen(true);
            }}
          >
            إضافة قاعدة
          </Button>
        }
      >
        {rules.length === 0 ? (
          <Empty title="لا قواعد" hint="أضف قاعدة لقبول ردود الشبكة." />
        ) : (
          <ListTable headers={['الشبكة', 'الخدمة', 'نوع المطابقة', 'النص', 'النتيجة', 'الرقم مطلوب', 'الحالة', '']}>
            <tbody>
              {rules.map((r) => (
                <tr key={str(r.id)}>
                  <td>{str(r.company) === 'syriatel' ? 'سيريتل' : str(r.company).toUpperCase()}</td>
                  <td>{OP_LABELS[str(r.operation_kind)] ?? str(r.operation_kind)}</td>
                  <td>{MATCH_LABELS[str(r.match_type)] ?? str(r.match_type)}</td>
                  <td>
                    <code dir="auto">{str(r.pattern)}</code>
                  </td>
                  <td>
                    <StatusBadge status={str(r.result_type) === 'success' ? 'success' : 'failed'} />
                  </td>
                  <td>{Number(r.require_target) === 1 ? 'نعم' : 'لا'}</td>
                  <td>{Number(r.is_active) === 1 ? 'فعالة' : 'موقفة'}</td>
                  <td>
                    <div className="table-actions">
                      <button
                        type="button"
                        className="icon-btn"
                        title="تعديل"
                        onClick={() => {
                          setRuleForm({ ...r });
                          setRuleOpen(true);
                        }}
                      >
                        <Icon name="edit" size={16} />
                      </button>
                      <button
                        type="button"
                        className="icon-btn danger"
                        title="حذف"
                        onClick={() => void deleteRule(r.id)}
                      >
                        <Icon name="close" size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </ListTable>
        )}
      </SectionCard>

      <UssdCatalogPanels onMessage={setMsg} />

      <FilterCard title="تصفية أجهزة التنفيذ" icon={<Icon name="settings" size={22} />}>
        <ProSearch value={q} onChange={setQ} placeholder="اسم الجهاز أو الموديل أو الشريحة" />
      </FilterCard>

      <SectionCard
        title="أجهزة وشرائح التنفيذ"
        subtitle="الاتصال يعني وصول نبضة خلال آخر دقيقتين؛ الجاهزية تشمل الخدمة والصلاحيات والشريحة أيضاً."
        icon={<Icon name="device" size={24} />}
      >
        {filteredDevices.length === 0 ? (
          <Empty title="لا أجهزة" hint="سجّل جهازاً من تطبيق منفذ USSD على هاتف الإدارة." />
        ) : (
          <div className="list-stack top-space">
            {filteredDevices.map((d) => {
              const sims = Array.isArray(d.sims) ? (d.sims as JsonMap[]) : [];
              const deleted = Boolean(d.deleted_at);
              const draft = deviceDraft(d);
              return (
                <article key={str(d.id)} className="executor-device-card section-card">
                  <div className="executor-device-head list-head">
                    <div>
                      <strong>{str(d.device_name, 'جهاز غير معروف')}</strong>
                      <small className="muted block">
                        {str(d.manufacturer)} {str(d.model)} · #{str(d.id)} · {str(d.app_version, 'إصدار غير معروف')}
                      </small>
                      <small className="muted block">
                        آخر اتصال: {formatDateTime(d.last_seen_at)} · آخر طلب للطابور:{' '}
                        {formatDateTime(d.last_poll_at)}
                      </small>
                    </div>
                    <div className="action-row wrap">
                      <StatusBadge status={Number(d.is_online) === 1 ? 'online' : 'offline'} />
                      {deleted ? <StatusBadge status="deleted" /> : null}
                      {!deleted && Number(d.is_active) === 0 ? <StatusBadge status="blocked" /> : null}
                    </div>
                  </div>

                  {!deleted ? (
                    <>
                      <div className="form-grid two-columns tight top-space">
                        <Input
                          label="اسم الجهاز"
                          value={str(draft.device_name, '')}
                          onChange={(e) => patchDevice(Number(d.id), d, { device_name: e.target.value })}
                        />
                        <Select
                          label="قرار الإدارة"
                          value={String(Number(draft.is_active ?? 0))}
                          onChange={(v) => patchDevice(Number(d.id), d, { is_active: Number(v) })}
                          options={[
                            { value: '1', label: 'مفعّل' },
                            { value: '0', label: 'معطّل — لا يستلم طلبات جديدة' },
                          ]}
                        />
                      </div>
                      <div className="action-row wrap">
                        <Button type="button" disabled={busy} onClick={() => void saveDevice(d)}>
                          حفظ الجهاز
                        </Button>
                        <Button
                          type="button"
                          variant="secondary"
                          disabled={busy || Number(d.is_active) !== 1}
                          onClick={() => void disableDevice(d)}
                        >
                          إيقاف من الإدارة
                        </Button>
                        <Button type="button" variant="danger" onClick={() => void deleteDevice(d)}>
                          حذف الجهاز
                        </Button>
                      </div>
                    </>
                  ) : (
                    <Button type="button" variant="secondary" onClick={() => void restoreDevice(d)}>
                      استعادة الجهاز
                    </Button>
                  )}

                  {!deleted && sims.length > 0 ? (
                    <div className="executor-sims-grid top-space">
                      {sims.map((sim) => {
                        const sd = simDraft(sim);
                        return (
                          <section key={str(sim.id)} className="executor-sim-card">
                            <div className="list-head">
                              <strong>{str(sim.sim_label, `SIM ${Number(sim.sim_slot) + 1}`)}</strong>
                              <StatusBadge status={simReady(d, sim) ? 'approved' : 'pending'} />
                            </div>
                            <small className="muted">
                              المنفذ {Number(sim.sim_slot) + 1} · {str(sim.carrier_name ?? sim.display_name, 'شبكة غير معروفة')}
                            </small>
                            <Form onSubmit={() => void saveSim(sim)}>
                              <Input
                                label="اسم الشريحة"
                                value={str(sd.sim_label, '')}
                                onChange={(e) => patchSim(Number(sim.id), sim, { sim_label: e.target.value })}
                              />
                              <Select
                                label="الشركة"
                                value={str(sd.company, 'other')}
                                onChange={(v) => patchSim(Number(sim.id), sim, { company: v })}
                                options={[
                                  { value: 'syriatel', label: 'سيريتل' },
                                  { value: 'mtn', label: 'MTN' },
                                  { value: 'other', label: 'أخرى' },
                                ]}
                              />
                              <Input
                                label="رمز التحويل (اختياري)"
                                value={str(sd.secret_code, '')}
                                onChange={(e) => patchSim(Number(sim.id), sim, { secret_code: e.target.value })}
                                placeholder={Number(sim.has_secret) === 1 ? '•••• محفوظ' : ''}
                              />
                              <Select
                                label="حالة الشريحة"
                                value={String(Number(sd.is_active ?? 0))}
                                onChange={(v) => patchSim(Number(sim.id), sim, { is_active: Number(v) })}
                                options={[
                                  { value: '1', label: 'مفعّلة' },
                                  { value: '0', label: 'معطّلة' },
                                ]}
                              />
                              <Button type="submit" disabled={busy}>
                                حفظ الشريحة
                              </Button>
                            </Form>
                          </section>
                        );
                      })}
                    </div>
                  ) : null}
                </article>
              );
            })}
          </div>
        )}
      </SectionCard>

      <Modal open={ruleOpen} title={Number(ruleForm.id) > 0 ? 'تعديل قاعدة الرد' : 'إضافة قاعدة رد'} onClose={() => setRuleOpen(false)}>
        <Form onSubmit={() => void saveRule()}>
          <Select
            label="الشبكة"
            value={str(ruleForm.company, 'syriatel')}
            onChange={(v) => setRuleForm((f) => ({ ...f, company: v }))}
            options={[
              { value: 'syriatel', label: 'سيريتل' },
              { value: 'mtn', label: 'MTN' },
            ]}
          />
          <Input
            label="الخدمة (* = الكل)"
            value={str(ruleForm.operation_kind, '*')}
            onChange={(e) => setRuleForm((f) => ({ ...f, operation_kind: e.target.value }))}
          />
          <Select
            label="نوع المطابقة"
            value={str(ruleForm.match_type, 'contains')}
            onChange={(v) => setRuleForm((f) => ({ ...f, match_type: v }))}
            options={[
              { value: 'contains', label: 'يحتوي' },
              { value: 'starts_with', label: 'يبدأ بـ' },
              { value: 'regex', label: 'Regex' },
            ]}
          />
          <Input
            label="النص"
            value={str(ruleForm.pattern, '')}
            onChange={(e) => setRuleForm((f) => ({ ...f, pattern: e.target.value }))}
            required
          />
          <Select
            label="النتيجة"
            value={str(ruleForm.result_type, 'success')}
            onChange={(v) => setRuleForm((f) => ({ ...f, result_type: v }))}
            options={[
              { value: 'success', label: 'نجاح' },
              { value: 'failure', label: 'فشل' },
            ]}
          />
          <Select
            label="الرقم مطلوب في الرد"
            value={String(Number(ruleForm.require_target ?? 0))}
            onChange={(v) => setRuleForm((f) => ({ ...f, require_target: Number(v) }))}
            options={[
              { value: '1', label: 'نعم' },
              { value: '0', label: 'لا' },
            ]}
          />
          <Select
            label="الحالة"
            value={String(Number(ruleForm.is_active ?? 1))}
            onChange={(v) => setRuleForm((f) => ({ ...f, is_active: Number(v) }))}
            options={[
              { value: '1', label: 'فعالة' },
              { value: '0', label: 'موقفة' },
            ]}
          />
          <Input
            label="الترتيب"
            type="number"
            value={String(ruleForm.sort_order ?? 0)}
            onChange={(e) => setRuleForm((f) => ({ ...f, sort_order: Number(e.target.value) || 0 }))}
          />
          <Button type="submit" disabled={busy}>
            حفظ القاعدة
          </Button>
        </Form>
      </Modal>
    </ScreenFrame>
  );
}
